import { CLOSED_CASE_STATUSES, DomainCaseStatus } from './enums';

/**
 * What may be done to a case, and from which states.
 *
 * ── why a table ────────────────────────────────────────────────────────────
 *
 * These rules were seven separate `if`s spread through a two-thousand-line
 * service, each phrasing its refusal differently and each having to be found
 * before the set could be read at all. "Which actions can a held case accept?"
 * had no answer short of reading the whole file, and two of the checks had
 * already drifted apart: taking a case tested a list of states, holding one
 * tested the closed group, and channel work tested the closed group again with
 * a different message.
 *
 * As data it is nine lines and can be read in one sitting, tested by
 * enumeration rather than by driving the product, and shown to whoever asks
 * what the console's buttons are allowed to do.
 *
 * ── what this is not ───────────────────────────────────────────────────────
 *
 * Not the writes. `take` also stops collection, `close` also splits live links
 * to the next round, and both do it in one claim so two tabs cannot both win —
 * that belongs with the code that writes, not here. This answers one question:
 * may this action be attempted against a case in this state.
 */
export type CaseAction =
  | 'take'
  | 'hold'
  | 'unhold'
  | 'assign'
  | 'close'
  /** Sending, replying, or marking a channel done, declined, no-answer, closed. */
  | 'channel_work';

export interface CaseTransition {
  /** The states the action may be attempted from. */
  readonly from: readonly DomainCaseStatus[];
  /**
   * The state the case ends in, where the action always lands in one. `take`
   * and `unhold` do; `close` picks from the reason, `assign` and channel work
   * leave the status where it was.
   */
  readonly to?: DomainCaseStatus;
  /** What to tell somebody who tried it anyway. `%s` is the current status. */
  readonly refusal: string;
}

const OPEN = [
  DomainCaseStatus.PENDING,
  DomainCaseStatus.HOLD,
  DomainCaseStatus.IN_PROGRESS,
] as const;

export const CASE_TRANSITIONS: Readonly<Record<CaseAction, CaseTransition>> = {
  // Not from HOLD: a held case is parked on purpose, and taking it would step
  // over whoever parked it without lifting the hold or saying why.
  take: {
    from: [DomainCaseStatus.PENDING, DomainCaseStatus.IN_PROGRESS],
    to: DomainCaseStatus.IN_PROGRESS,
    refusal: 'Case is %s, cannot be taken',
  },
  hold: { from: OPEN, refusal: 'Case is %s, nothing to hold' },
  unhold: {
    from: [DomainCaseStatus.HOLD],
    // Off hold is back to being worked. It used to come back to `in_review`
    // when nothing had been sent yet, which is a state that no longer exists.
    to: DomainCaseStatus.IN_PROGRESS,
    refusal: 'Case is not on hold',
  },
  assign: { from: OPEN, refusal: 'Case is %s, there is nothing left to assign' },
  close: { from: OPEN, refusal: 'Case is already %s' },
  channel_work: { from: OPEN, refusal: 'Case is %s' },
};

/** Whether the action may be attempted against a case in this state. */
export function caseAllows(action: CaseAction, status: DomainCaseStatus | string): boolean {
  return CASE_TRANSITIONS[action].from.includes(status as DomainCaseStatus);
}

/**
 * Why it may not, in the words the console shows. `what` names the attempted
 * work where the sentence needs it ("Case is done, cannot send dmca").
 */
export function caseRefusal(
  action: CaseAction,
  status: DomainCaseStatus | string,
  what?: string,
): string {
  const said = CASE_TRANSITIONS[action].refusal.replace('%s', String(status));
  return what ? `${said}, cannot ${what}` : said;
}

/** A case that will never move again, asked as a question rather than a list. */
export function caseIsClosed(status: DomainCaseStatus | string): boolean {
  return CLOSED_CASE_STATUSES.includes(status as DomainCaseStatus);
}
